import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {DayKey} from "../../../_shared/time/company-day";
import {map, Observable} from "rxjs";
import {EmployeeDto} from "../x-models/employee-dto";
import {EmployeeOdto} from "../x-models/employee-odto";
import {EmployeeNl} from "../x-models/employee-nl";
import {CompositionMinimal} from "../../../models/composition/composition-minimal";
import {SocialTypes} from "../../../x-models/x-enums/social-types";
import {EventDto} from "../../../models/event/event-dto";
import {DtoBaseHttpService} from "../../../_services/dto-services/dto-base-http.service";
import {ServerEndpoints} from "../../../server.endpoints";
import {EmployeeStatsExtended} from "../../../models/company/employee-stats-extended";
import {EmployeeShiftOverviewDto} from "../_models/employee/employee-shift-overview-dto";
import {EmploymentRelationshipLinkerDto} from "../x-models/employment-relationship-linker-dto";
import {EmployeeSalesEventDto, EmployeeSalesOrder} from "../x-models/employee-sales-event";
import {OrderHelper} from "../../location/_services/order-helper";
import {EmploymentPeriodUpdate} from "../x-models/employment-period";

@Injectable({
  providedIn: 'root'
})
export class EmployeeHttpService extends DtoBaseHttpService<EmployeeDto> {
  UrlPath: string = "/Company/Employee";

  constructor(httpClient: HttpClient,
              @Inject("BASE_URL") baseUrl: string) {
    super(baseUrl, httpClient)
  }

  /**
   * List employees. By default only the currently employed; `all = true` opts into former employees
   * ("nicht mehr beschäftigt") for the merged employees page and historical displays.
   */
  list(relationshipId: string, all: boolean = false): Observable<EmployeeDto[]> {
    return this.HttpClient.get<EmployeeDto[]>(this.BaseUrl + ServerEndpoints.Company_Employee_List
      + "?employmentRelationship=" + relationshipId + (all ? "&all=true" : ""));
  }

  /**
   * List employees with stats. With `start` the server windows the roster to the timeframe around that date
   * (shift-maker: former employees of a past week included, future stints excluded); without it, the currently
   * employed. `all = true` opts into former employees — for surfaces resolving names on historical data.
   *
   * `stats = false` asks for the lean projection (#457): same roster, but without `employeeStats`,
   * `employeeAttributes` and `telegram`. Those three are ~63% of the payload, and computing the statistics costs
   * the server every shift response of every employee in the roster plus a per-shard event/location resolution —
   * on a long-lived tenant that is over a second for a list only used to turn an id into a name. Pass `false`
   * from every name-resolution surface; leave it `true` only where the shift-maker-style stats are actually read.
   */
  listNl(start: DayKey | undefined = undefined, all: boolean = false,
         stats: boolean = true): Observable<EmployeeNl[]> {
    const params = [
      // A company calendar day (`yyyy-MM-dd`): the server windows the roster to that day's company MONTH.
      start ? "start=" + start : "",
      all ? "all=true" : "",
      stats ? "" : "stats=false"
    ].filter(p => p !== "").join("&");
    return this.HttpClient.get<EmployeeNl[]>(this.BaseUrl + ServerEndpoints.Company_Employee_ListNl
      + (params ? "?" + params : ""));
  }

  /** Get one employee; `all = true` also resolves former employees (details/settings of "Ehemalige"). */
  get(id: string, all: boolean = false): Observable<EmployeeDto> {
    return this.HttpClient.get<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_Get + "?id=" + id
      + (all ? "&all=true" : ""));
  }

  /**
   * Create an employee.
   *
   * There is no `sendPasswordReset` any more (#279): creating an employee no longer mails anybody a
   * password-reset link. A way in is an INVITATION, issued separately once the employee exists (it has to name
   * them) — see `GrantHttpService.inviteEmployeeLink` and the create form's "Einladung erstellen".
   */
  add(first: string, last: string, employmentStart?: Date, email?: string): Observable<EmployeeDto> {
    if (email?.isNullOrWhitespace() == true) email = undefined;

    return this.HttpClient.post<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_Add, {
      "FirstName": first,
      "LastName": last,
      // Begin of the initial employment period; omitted ⇒ employment starts now.
      "EmploymentStart": employmentStart?.toISOString(),
      "EmailAddress": email
    })
  }

  /**
   * End an employment: closes the open employment period, revokes the employee's sessions and frees future
   * shifts. Replaces the retired delete — no employee data is ever deleted; the employee stays visible as
   * "nicht mehr beschäftigt". `endDate` is a company-local **calendar day** (`yyyy-MM-dd`); the server resolves
   * it in the company time zone (today/omitted ⇒ now, a future day ⇒ end of that day). Never send an instant —
   * the day boundary must be interpreted server-side so it matches the company's time zone.
   */
  endEmployment(id: string, endDate?: string): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_EndEmployment
      + "?id=" + id + (endDate ? "&endDate=" + endDate : ""), {});
  }

  /**
   * Rehire a former employee: opens a new employment period. `startDate` is a company-local calendar day
   * (`yyyy-MM-dd`), resolved server-side (today/omitted ⇒ now, future ⇒ that day's local midnight). 409 on overlap.
   */
  rehire(id: string, startDate?: string): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_Rehire
      + "?id=" + id + (startDate ? "&startDate=" + startDate : ""), {});
  }

  /**
   * Edit a single employment period's dates. `start`/`end` are company-local **calendar days** (`yyyy-MM-dd`);
   * the server resolves them in the company time zone (start-of-day / through-end-of-day). Overlap and
   * end-before-start are guarded server-side.
   */
  updateEmploymentPeriod(id: string, update: EmploymentPeriodUpdate): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_UpdateEmploymentPeriod
      + "?id=" + id, update);
  }

  employeeOrders(id: string): Observable<EmployeeOdto[]> {
    return this.HttpClient.get<EmployeeOdto[]>(this.BaseUrl + ServerEndpoints.Inventory_Event_GetEmployeeOrders + "?id=" + id);
  }

  changeName(id: string, firstname: string, lastname: string): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_ChangeName, {
      "Id": id,
      "FirstName": firstname,
      "LastName": lastname
    })
  }

  setCanLogin(id: string, allow: boolean): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_AllowEmployeeLogin + "?id=" + id + "&allow=" + allow, {});
  }

  /**
   * Admin reset of an employee's critical-step-up PIN: clears it, forcing the employee to re-enrol a new one in
   * self-service. Admins can never set a chosen value — only the employee knows their PIN — so this is the admin's
   * recovery path for a forgotten PIN.
   */
  resetPin(id: string): Observable<void> {
    return this.HttpClient.put<void>(this.BaseUrl + ServerEndpoints.Company_Employee_ResetPin + "?id=" + id, {});
  }

  /**
   * Enable or disable an employee for employee identification (the PIN critical step-up). Disabling also clears any
   * configured PIN server-side. Returns the updated employee.
   */
  setEmployeeIdentification(id: string, enabled: boolean): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(
      this.BaseUrl + ServerEndpoints.Company_Employee_SetEmployeeIdentification + "?id=" + id + "&enabled=" + enabled, {});
  }

  resetSocial(id: string, name: string): Observable<EmployeeDto> {
    return this.HttpClient.delete<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_ResetSocial + "?id=" + id + "&name=" + name);
  }

  employeeSalesList(id: string, start: string, end: string): Observable<CompositionMinimal[]> {
    start += "T00:00:00.000000";
    end += "T23:59:59.999999";
    return this.HttpClient.get<CompositionMinimal[]>(this.BaseUrl +
      ServerEndpoints.Company_Employee_EmployeeSalesList +
      "?employeeId=" + id +
      "&start=" + start +
      "&end=" + end);
  }

  /**
   * One employee's own consumption in a date range, grouped by the event it happened at — the „Getränke"
   * tab's Event view.
   *
   * **No `?fields=` projection any more (Feature #760 / Story #763).** It used to send
   * `fields=*,Orders{…}`, and the leading `*` is the worst shape the server's filter has: the star copies
   * every scalar of the ROOT **boxed**, so the typed contract — and with it every property-level
   * `[JsonConverter]` and `[JsonIgnore]` — is gone before Newtonsoft writes anything (Bug #747). It also
   * shipped whatever `EventEdto` happened to carry rather than what this view renders. The endpoint answers
   * the fixed {@link EmployeeSalesEventDto} now.
   *
   * @param id the employee whose consumption to list — the FILTER, not the shape of the answer.
   * @param start first day, `yyyy-MM-dd`.
   * @param end last day, `yyyy-MM-dd`, inclusive.
   */
  employeeSalesOrders(id: string, start: string, end: string): Observable<EmployeeSalesEventDto[]> {
    start += "T00:00:00.000000";
    end += "T23:59:59.999999";
    return this.HttpClient.get<EmployeeSalesEventDto[]>(this.BaseUrl +
      ServerEndpoints.Company_Employee_EmployeeSalesOrders +
      "?employeeId=" + id +
      "&start=" + start +
      "&end=" + end
    ).pipe(map((events) => {
      (events ?? []).forEach(event => {
        event.orders ??= [];
        event.orders.forEach(order => {
          EmployeeHttpService.normalizeOrder(order);
          // The #142 display prices this view renders: `setCompositionPrices` writes the distributed unit
          // price onto each line's article as `oPrice`, `setOrderBookingValues` folds the row's „Gesamt".
          OrderHelper.setCompositionPrices(order);
          OrderHelper.setOrderBookingValues(order);
        });
      });

      return events ?? [];
    }));
  }

  /**
   * Fills in the collections this view dereferences **without a guard**, for one transaction as it comes off
   * the wire — the same boundary rule `EventHttpService` applies to the event-details payload (#760).
   *
   * A required TypeScript property is a statement of intent about a contract the client does not control;
   * Bug #440 is the proof that a real payload can disagree with it, and it disagreed on THIS endpoint too —
   * its projection was the second one that never named `ModificationLines`. So the `?? []` that used to sit
   * in the template runs here instead: once per payload rather than once per change-detection pass, and for
   * every consumer rather than only the template that remembered to write it.
   *
   * Deliberately not normalised: `composition` (a missing article is a broken line, not a trimmed field) and
   * the genuinely optional `oPrice` (client-derived, and `undefined` is its documented "no distribution
   * applies" value).
   */
  private static normalizeOrder(order: EmployeeSalesOrder): EmployeeSalesOrder {
    // `??=` rather than `||=`: it catches BOTH an absent key and an explicit `null`, and leaves a
    // legitimately empty array alone.
    order.compositions ??= [];
    order.businessCaseInformations ??= [];
    order.compositions.forEach(line => line.modificationLines ??= []);
    return order;
  }


  setSocial(id: string, name: string, value: string | undefined): Observable<EmployeeDto> {
    return this.HttpClient.put<EmployeeDto>(this.BaseUrl + ServerEndpoints.Company_Employee_SetSocial + "?id=" + id, {
      name: name,
      value: value
    })
  }

  verify(id: string, type: SocialTypes) {
    return this.HttpClient.get(this.BaseUrl + ServerEndpoints.Company_Employee_RequestVerification + "?socialType=" + type.valueOf() + "&id=" + id);
  }

  /**
   * An employee's shifts whose EVENT starts on a company calendar day in `[start, end]` (`yyyy-MM-dd`, both
   * inclusive). The days are sent verbatim; the server resolves them in the company time zone (#645). Never send
   * browser-local midnight as an ISO instant here — that was correct only for viewers inside the company's zone.
   */
  getEmployeeShiftList(id: string, start: DayKey, end: DayKey): Observable<EmployeeShiftOverviewDto> {
    const params = new HttpParams().set('id', id).set('start', start).set('end', end);
    return this.HttpClient.get<EmployeeShiftOverviewDto>(this.BaseUrl + ServerEndpoints.Company_Employee_GetEmployeeShiftList, {params});
  }

  setPublicProperty(id: string, name: string, value: string): Observable<EventDto> {
    return this.HttpClient.put<EventDto>(this.BaseUrl + ServerEndpoints.Company_Employee_SetPublicProperty + "?id=" + id, {
      name: name,
      value: value
    })
  }

  downloadIndividualDetails(id: string, start: Date, end: Date) {

  }

  getStats(employee: string): Observable<EmployeeStatsExtended> {
    return this.HttpClient.get<EmployeeStatsExtended>(`${this.BaseUrl}${ServerEndpoints.Company_Employee_GetStats_$employee$.dictReplace({employee: employee})}`);
  }

  saveValue(id: string, name: string, newPropertyValue: any): Observable<EmployeeDto> {
    if (newPropertyValue == "") newPropertyValue = undefined;

    return this.HttpClient.post<EmployeeDto>(`${this.BaseUrl}${ServerEndpoints.Company_Employee_SetValue_$employee$.dictReplace({employee: id})}`, {
      name: name,
      value: newPropertyValue
    })
  }


  getEmploymentRelationships(id: string): Observable<EmploymentRelationshipLinkerDto[]> {
    return this.HttpClient.get<EmploymentRelationshipLinkerDto[]>(`${this.BaseUrl}${ServerEndpoints.Company_Employee_GetEmploymentRelationships_$id$.dictReplace({id: id})}`);
  }
}
